import { useMemo } from "react";
import { useLocation } from "react-router-dom";
import { NAVEGACAO } from "../config/navegacao";
import { useAutenticacaoContext } from "../contexts/AutenticacaoContext";
import { IGrupoNavegacao, IItemNavegacao, IModuloNavegacao } from "../interfaces/INavegacao";
import { ChaveNotificacao } from "../interfaces/INotificacoes";

export interface IItemPlano {
    titulo: string;
    url: string;
    modulo: IModuloNavegacao;
    /** Títulos dos itens-pai, quando o item está aninhado. */
    caminho: string[];
    /** Contador de notificações do destino, quando houver. */
    notificacao?: ChaveNotificacao;
}

/** A rota casa com a url quando é ela mesma ou está abaixo dela. */
export const rotaCorresponde = (pathname: string, url: string) =>
    pathname === url || pathname.startsWith(`${url}/`);

/**
 * Resolve a navegação visível para o perfil autenticado e descobre onde o
 * usuário está (módulo, item e itens-pai a manter abertos).
 */
export function useNavegacao() {
    const { pathname } = useLocation();
    const { perfil, verificarAcessoModulo } = useAutenticacaoContext();

    const grupos = useMemo<IGrupoNavegacao[]>(() => {
        const temAcesso = (modulos?: string[]) =>
            !modulos?.length || modulos.some(nome => verificarAcessoModulo(nome));

        const filtrarItens = (itens: IItemNavegacao[]): IItemNavegacao[] =>
            itens.reduce<IItemNavegacao[]>((lista, item) => {
                if (!temAcesso(item.modulos)) return lista;

                const subitens = item.subitens ? filtrarItens(item.subitens) : undefined;
                // Item que só existe para agrupar some quando nenhum filho sobrevive ao filtro.
                if (item.subitens?.length && !subitens?.length) return lista;

                lista.push(subitens ? { ...item, subitens } : item);
                return lista;
            }, []);

        return NAVEGACAO.reduce<IGrupoNavegacao[]>((lista, grupo) => {
            const modulos = grupo.modulos.reduce<IModuloNavegacao[]>((modulosVisiveis, modulo) => {
                if (!temAcesso(modulo.modulos)) return modulosVisiveis;

                const itens = modulo.itens ? filtrarItens(modulo.itens) : undefined;
                if (modulo.itens?.length && !itens?.length) return modulosVisiveis;

                modulosVisiveis.push(itens ? { ...modulo, itens } : modulo);
                return modulosVisiveis;
            }, []);

            if (modulos.length) lista.push({ ...grupo, modulos });
            return lista;
        }, []);
        // `verificarAcessoModulo` é recriado a cada render — o que importa é o perfil.
    }, [perfil]);

    const moduloAtivo = useMemo(() => {
        let ativo: IModuloNavegacao | undefined;
        let maiorPrefixo = -1;

        grupos.forEach(grupo =>
            grupo.modulos.forEach(modulo => {
                (modulo.prefixos ?? [modulo.url]).forEach(prefixo => {
                    if (rotaCorresponde(pathname, prefixo) && prefixo.length > maiorPrefixo) {
                        maiorPrefixo = prefixo.length;
                        ativo = modulo;
                    }
                });
            })
        );

        return ativo;
    }, [grupos, pathname]);

    const { urlAtiva, caminhoAtivo } = useMemo(() => {
        const resultado: { urlAtiva?: string; caminhoAtivo: string[] } = { caminhoAtivo: [] };
        if (!moduloAtivo?.itens?.length) return resultado;

        let maiorUrl = -1;
        const percorrer = (itens: IItemNavegacao[], ancestrais: string[]) => {
            itens.forEach(item => {
                if (rotaCorresponde(pathname, item.url) && item.url.length > maiorUrl) {
                    maiorUrl = item.url.length;
                    resultado.urlAtiva = item.url;
                    resultado.caminhoAtivo = ancestrais;
                }

                if (item.subitens?.length) percorrer(item.subitens, [...ancestrais, item.url]);
            });
        };

        percorrer(moduloAtivo.itens, []);
        return resultado;
    }, [moduloAtivo, pathname]);

    /** Todos os destinos navegáveis em lista plana — usado pela busca do menu. */
    const itensPlanos = useMemo<IItemPlano[]>(() => {
        const lista: IItemPlano[] = [];

        grupos.forEach(grupo =>
            grupo.modulos.forEach(modulo => {
                if (!modulo.itens?.length) {
                    lista.push({
                        titulo: modulo.titulo,
                        url: modulo.url,
                        modulo,
                        caminho: [],
                        notificacao: modulo.notificacao,
                    });
                    return;
                }

                const percorrer = (itens: IItemNavegacao[], caminho: string[]) => {
                    itens.forEach(item => {
                        if (item.subitens?.length) {
                            percorrer(item.subitens, [...caminho, item.titulo]);
                            return;
                        }

                        lista.push({
                            titulo: item.titulo,
                            url: item.url,
                            modulo,
                            caminho,
                            notificacao: item.notificacao,
                        });
                    });
                };

                percorrer(modulo.itens, []);
            })
        );

        return lista;
    }, [grupos]);

    return { grupos, moduloAtivo, urlAtiva, caminhoAtivo, itensPlanos };
}
